import {
  concatenateDataTypeNameWithSelectedSegmentType,
  getCountryFromSideFilters,
  getProductGroupFromSideFilters,
  segmentsFilterWithExcludeFilterKeys,
} from '@utils/FilterUtils';
import { FilterKeys, PageName, PageType } from '@utils/pageConfig/Types';
import { concatenateSelectedFeatureName, doAmplitudeAnalyticsTracking } from '../PageAnalytics';
import {
  AnalyticTrackingBasePropertiesObj,
  FilterName,
  FilterType,
  TrackingGeneralProp,
} from './AnalyticTrackingBasePropertiesObj';

/**
 Example of tracking for - Global Filter

 analytics.track("Filter Applied Details", {
 "access": "Retail Insights",
 "locationId": "Germany",
 "productGroup": "PTV", //"All" or "PTV, AIR CONDITIONER"
 "categoryChosen": "Brands",
 "categoryCombination": "Brands, Avg Price Max",
 "filterName": "Global Filter",
 "filterType": "Platform",
 "optionChosen": "Samsung",
 "pageName": "Products",
 "section": Retail Intelligence",
 "subsection": "Products",
 "retailerDataFor": "xxxxx"
 })

 analytics.track("Filter Applied Details", {
 "access": "Retail Insights",
 "country": "Germany",
 "productGroup": "PTV", //"All" or "PTV, AIR CONDITIONER"
 "categoryChosen": "Avg Price Max",
 "categoryCombination": "Brands, Avg Price Max",
 "filterName": "Global Filter",
 "filterType": "Platform",
 "optionChosen": "1000",
 "pageName": "Products",
 "section": Retail Intelligence",
 "subsection": "Products",
 "retailerDataFor": "xxxxx"
 })

 analytics.track("Filter Applied", {
 "access": "Retail Insights",
 "country": "Germany",
 "productGroup": "PTV", //"All" or "PTV, AIR CONDITIONER"
 "categoryCombination": "Brands, Avg Price Max", //all options chosen
 "filterName": "Global Filter",
 "filterType": "Platform",
 "pageName": "Products",
 "retailerDataFor": "xxxxx",
 "section": "Retail Intelligence",
 "subsection": "Products"
 })
 **/

export const GLOBAL_FILTER_APPLIED_TRACKING_TITLE = 'Filter Applied';
export const GLOBAL_FILTER_DETAILS_APPLIED_TRACKING_TITLE = 'Filter Applied Details';

//Extend base properties
export interface GlobalFiltersTracking extends AnalyticTrackingBasePropertiesObj {
  categoryChosen?: string;
  categoryCombination: string;
  filterName: string;
  filterType: string;
  optionChosen?: string;
}

export interface GlobalFiltersTrackingFunctionParam {
  eventTitle: string;
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  filterName: FilterName;
  filterType: FilterType;

  access: Object;
}

export const globalFiltersTrackingFunction = (param: GlobalFiltersTrackingFunctionParam) => {
  const { sideFilters, pageName, retailerClientState, pageType, filterName, filterType, track, eventTitle, access } =
    param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const filteredSegments = segmentsFilterWithExcludeFilterKeys(selectedSideFilter, [
    FilterKeys.COUNTRY,
    FilterKeys.PRODUCT_GROUP,
  ]);

  const featureCombination = concatenateDataTypeNameWithSelectedSegmentType(filteredSegments);

  //Start main filter tracking
  const globalFiltersMainProperties: GlobalFiltersTracking = {
    access: access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label),
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName: pageName,
    retailerDataFor: retailerClientState.clientName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: pageName,
    categoryCombination: featureCombination,
    filterName: filterName,
    filterType: filterType,
  };

  doAmplitudeAnalyticsTracking(eventTitle, track, globalFiltersMainProperties);
  //End main filter tracking

  //Start details filter tracking
  //if any segment being choose
  if (filteredSegments.length) {
    filteredSegments.forEach((filter) => {
      const globalFiltersDetailsProperties: GlobalFiltersTracking = {
        access: access,
        locationId: appliedFiltersCountry?.options.map((options) => options.label),
        itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
        pageName: pageName,
        retailerDataFor: retailerClientState.clientName,
        section: TrackingGeneralProp.SECTION_TRACKING_PROP,
        subsection: pageName,
        categoryChosen: filter.feature,
        categoryCombination: featureCombination,
        optionChosen: concatenateSelectedFeatureName(filter),
        filterName: filterName,
        filterType: filterType,
      };

      doAmplitudeAnalyticsTracking(GLOBAL_FILTER_DETAILS_APPLIED_TRACKING_TITLE, track, globalFiltersDetailsProperties);
    });
  }
  //End details filter tracking
};
